import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { CIBLE_MIN, COULEURS, IDENTITE, OMBRES, POLICE_TITRES, RAYONS } from './styles.js';

/**
 * Identité visuelle de la coquille, reprise de la démo et des maquettes : bandeau marine, logo triangle,
 * filet orange, navigation dans le bandeau, menu replié sur mobile, titres en serif côté équipe.
 */
export function LogoMaat() {
  return (
    <svg data-logo="maat" role="img" aria-label="Cabinet MAAT" viewBox="0 0 30 26" width={30} height={26} style={{ flex: 'none' }}>
      <path d="M15 1 L29 25 L1 25 Z" fill={IDENTITE.corail} />
      <path d="M15 1 L29 25 L15 25 Z" fill={IDENTITE.corailClair} />
    </svg>
  );
}

const FEUILLE_COMMUNE =
  'a[data-lien-evitement]:not(:focus){position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}[data-repliable][hidden]{display:none!important}body{margin:0}a:focus-visible,button:focus-visible{outline:3px solid #FFFFFF;outline-offset:2px}main a:focus-visible,main button:focus-visible{outline-color:#34479A}';
const EQ = '[data-layout^="equipe"] main';
/**
 * Parité visuelle avec le prototype de démonstration (30/10). Les écrans écrivent leurs styles en ligne : ces règles, plus
 * spécifiques par construction, harmonisent sans toucher aux écrans. Le navigateur sérialise les couleurs en rgb() ; la forme
 * hexadécimale couvre le rendu serveur. Cartes : filet doux, 12 px, ombre légère. Boutons et champs : 10 px. Lignes de tableau : filet doux.
 */
const FEUILLE_PARITE_DEMO = [
  `${EQ} :is(div,section,article,aside,li,details,form)[style*="border-radius: 12px"],${EQ} :is(div,section,article,aside,li,details,form)[style*="border-radius:12px"]{box-shadow:${OMBRES.carte}}`,
  `${EQ} :is(div,section,article,aside,li,details,form,th,td)[style*="rgb(138, 143, 176)" i],${EQ} :is(div,section,article,aside,li,details,form,th,td)[style*="#8A8FB0" i]{border-color:${COULEURS.bordureDouce}!important}`,
  `${EQ} :is(button,a,input,select,textarea)[style*="border-radius: 8px"],${EQ} :is(button,a,input,select,textarea)[style*="border-radius:8px"]{border-radius:${RAYONS.bouton}px!important}`,
  `${EQ} button{font-weight:600}`,
  `${EQ} tbody tr:hover{background:#F0EFF5}`,
  `${EQ} table{border-collapse:separate;border-spacing:0;background:#FFFFFF;border:1px solid ${COULEURS.bordureDouce};border-radius:12px;overflow:hidden;box-shadow:${OMBRES.carte}}`,
  `${EQ} thead th{background:#F0EFF5}`,
  `${EQ} [data-carte-liste] table{box-shadow:none;border-color:transparent}`,
  `${EQ} span:is([style*="border-radius: 999px"],[style*="border-radius:999px"]):not([style*="background"]):is([style*="rgb(56, 61, 108)"],[style*="#383D6C" i]){background:#E8EBF7!important;border-color:transparent!important}`,
].join('');
const FEUILLE_EQUIPE = `[data-layout^="equipe"] h1{font-family:${POLICE_TITRES};font-weight:600;color:${COULEURS.marine}}[data-layout^="equipe"] h2,[data-layout^="equipe"] h3{color:${COULEURS.marine}}${FEUILLE_PARITE_DEMO}`;

/** Feuille de style de la coquille. Le serif n'est déclaré que pour l'équipe : le portail ne cite aucune police web. */
export function FeuilleTheme({ equipe }: { equipe: boolean }) {
  return <style data-theme="maat">{FEUILLE_COMMUNE + (equipe ? FEUILLE_EQUIPE : '')}</style>;
}

export interface ElementEntete {
  id: string;
  libelle: string;
  courant: boolean;
  lien: ReactNode;
}

const styleMarque: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minHeight: CIBLE_MIN,
  color: COULEURS.blanc,
  fontWeight: 600,
  fontSize: 20,
  letterSpacing: '0.06em',
};

export const styleBoutonEntete: CSSProperties = {
  minHeight: CIBLE_MIN,
  minWidth: CIBLE_MIN,
  padding: '0 16px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.6)',
  background: 'transparent',
  color: COULEURS.blanc,
  font: 'inherit',
  cursor: 'pointer',
};

/** Lien de navigation du bandeau : texte blanc, page courante soulignée en orange (décor) et en gras. */
export const styleLienEntete = (courant: boolean): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: CIBLE_MIN,
  minWidth: CIBLE_MIN,
  padding: '0 8px',
  color: COULEURS.blanc,
  textDecoration: 'none',
  fontSize: 15,
  fontWeight: courant ? 600 : 400,
  boxSizing: 'border-box',
  borderBottom: `3px solid ${courant ? IDENTITE.orange : 'transparent'}`,
});

export function initiales(nom: string): string {
  return (
    nom
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((m) => m.charAt(0).toUpperCase())
      .join('') || '?'
  );
}

export interface PropsEntete {
  mobile: boolean;
  nom: string;
  liens: ReactNode[];
  actions: ReactNode;
}

/**
 * Replie ou déplie le menu mobile. L'état vit dans le DOM (aria-expanded + attribut hidden), sans état React : la coquille
 * reste une fonction pure du routeur, et le menu fonctionne même avant l'hydratation.
 */
function basculerMenu(e: MouseEvent<HTMLButtonElement>): void {
  const bouton = e.currentTarget;
  const ouvrir = bouton.getAttribute('aria-expanded') !== 'true';
  bouton.setAttribute('aria-expanded', String(ouvrir));
  bouton
    .closest('header')
    ?.querySelectorAll<HTMLElement>('[data-repliable]')
    .forEach((el) => {
      el.hidden = !ouvrir;
    });
}

/** Bandeau marine à filet orange : marque, navigation, profil. Sur mobile équipe, la navigation est repliée derrière « Menu ». */
export function EnteteMaat({ mobile, nom, liens, actions }: PropsEntete) {
  return (
    <header
      data-bandeau="maat"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '4px 20px',
        padding: '8px 16px',
        background: COULEURS.marine,
        color: COULEURS.blanc,
        borderBottom: `3px solid ${IDENTITE.orange}`,
      }}
    >
      <div style={styleMarque}>
        <LogoMaat />
        <span>Lakou</span>
      </div>
      {mobile ? (
        <button
          type="button"
          aria-expanded={false}
          aria-controls="navigation-principale"
          onClick={basculerMenu}
          style={{ ...styleBoutonEntete, marginLeft: 'auto' }}
        >
          Menu
        </button>
      ) : null}
      <nav
        aria-label="Navigation principale"
        id="navigation-principale"
        data-repliable=""
        hidden={mobile}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          flexDirection: mobile ? 'column' : 'row',
          alignItems: mobile ? 'stretch' : 'center',
          gap: '0 4px',
          flex: '1 1 100%',
          order: 3,
        }}
      >
        {liens}
      </nav>
      <div
        data-repliable=""
        hidden={mobile}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 8,
          marginLeft: mobile ? 0 : 'auto',
          order: mobile ? 4 : 2,
          flex: mobile ? '1 1 100%' : undefined,
          paddingBottom: mobile ? 8 : 0,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: COULEURS.boutonPrincipal,
            color: COULEURS.blanc,
            display: 'grid',
            placeItems: 'center',
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          {initiales(nom)}
        </span>
        <span style={{ fontSize: 15 }}>{nom}</span>
        {actions}
      </div>
    </header>
  );
}
